"use client";
/**
 * Om os / About — Svendetid marketing about page content.
 * Language-parametrized so /about (da) and /en/about (en) share one source.
 * Uses the shared svendetid-ds design system (same nav + footer as
 * /features and /pricing).
 */
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    eyebrow: "Om os",
    h1: "Bygget til håndværket — af folk der kender det.",
    sub: "Svendetid er skabt for at give håndværksvirksomheder ét samlet sted til tilbud, job, tid og fakturering — så mestre og svende kan bruge tiden på arbejdet i stedet for papirarbejdet.",
    missionLabel: "Vores mission",
    missionH2: "Mindre papirarbejde. Mere håndværk.",
    missionBody: "Vi tror på, at den bedste software forsvinder i baggrunden. Svendetid samler de daglige opgaver — fra første forespørgsel til endelig faktura — i ét enkelt værktøj, der virker både på kontoret og ude på pladsen.",
    values: [
      { icon: "shield-checkmark-outline", title: "Enkelhed frem for alt", body: "Software skal kunne tages i brug uden et kursus. Vi designer for den travle hverdag." },
      { icon: "briefcase-outline", title: "Lavet til faget", body: "Hver funktion er bygget ud fra rigtige håndværkeres arbejdsgange — ikke generiske skabeloner." },
      { icon: "chatbubbles-outline", title: "Dansk support", body: "Vi sidder i Danmark og taler dit sprog — både fagligt og bogstaveligt." },
    ],
    ctaH2: "Vil du høre mere?",
    ctaBody: "Tag fat i os, eller prøv Svendetid gratis i dag.",
    contact: "Kontakt os",
    tryFree: "Prøv det gratis",
  },
  en: {
    eyebrow: "About us",
    h1: "Built for the trades — by people who know them.",
    sub: "Svendetid was created to give trades businesses one place for quotes, jobs, time and invoicing — so owners and tradespeople can spend their time on the work instead of the paperwork.",
    missionLabel: "Our mission",
    missionH2: "Less paperwork. More craft.",
    missionBody: "We believe the best software disappears into the background. Svendetid brings the day-to-day tasks together — from the first request to the final invoice — in one simple tool that works both in the office and out on the site.",
    values: [
      { icon: "shield-checkmark-outline", title: "Simplicity first", body: "Software should be usable without a course. We design for the busy working day." },
      { icon: "briefcase-outline", title: "Made for the trade", body: "Every feature is built around real tradespeople's workflows — not generic templates." },
      { icon: "chatbubbles-outline", title: "Danish support", body: "We're based in Denmark and speak your language — both professionally and literally." },
    ],
    ctaH2: "Want to hear more?",
    ctaBody: "Get in touch, or try Svendetid free today.",
    contact: "Contact us",
    tryFree: "Try it free",
  },
} as const;

export default function AboutContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const home = lang === "en" ? "/en" : "/";
  const contactHref = lang === "en" ? "/en/contact" : "/contact";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}`}</style>

      <LandingNav lang={lang} />

      {/* ===== HERO ===== */}
      <section style={{ background: "var(--green-900)", color: "var(--text-on-hero)", paddingTop: 64 }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "104px 32px 96px", textAlign: "center" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.eyebrow}</div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 54, lineHeight: 1.06, letterSpacing: "-0.025em", margin: "18px 0 0", color: "#fff", textWrap: "balance" }}>{t.h1}</h1>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "36em", margin: "22px auto 0" }}>{t.sub}</p>
        </div>
      </section>

      {/* ===== MISSION ===== */}
      <section style={{ padding: "96px 0 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 32px", textAlign: "center" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-strong)", marginBottom: 14 }}>{t.missionLabel}</div>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.missionH2}</h2>
          <p style={{ fontSize: 17, lineHeight: 1.65, color: "var(--text-muted)", margin: "16px auto 0", maxWidth: "34em" }}>{t.missionBody}</p>
        </div>
      </section>

      {/* ===== VALUES ===== */}
      <section style={{ padding: "48px 0 96px" }}>
        <div className="ds-3col" style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {t.values.map((v) => (
            <div key={v.title} style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column", gap: 15 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: "var(--green-100)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name={v.icon} size={24} color="var(--brand)" />
              </div>
              <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{v.title}</h3>
              <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 0 96px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ background: "var(--green-900)", borderRadius: 26, padding: "64px 48px", textAlign: "center" }}>
            <img src="/svendetid-logo.png" alt="" style={{ width: 64, height: 64, margin: "0 auto 18px", display: "block", opacity: 0.96 }} />
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, letterSpacing: "-0.02em", lineHeight: 1.08, margin: "0 auto", maxWidth: "18em", color: "#fff" }}>{t.ctaH2}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "30em", margin: "16px auto 0" }}>{t.ctaBody}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", marginTop: 32 }}>
              <Link href={contactHref} className="btn-blue" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.contact}<Icon name="arrow-forward" size={18} /></Link>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-ghost-dark" style={{ cursor: "pointer", background: "transparent", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.tryFree}</button></SignUpButton>
            </div>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/about" enHref="/en/about" />
    </div>
  );
}
